import React, { useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import type {
  HeatmapComparisonViewProps,
  VideoSegment,
  HeatmapColorScheme,
  ComparisonViewMode,
  DifferentialColorScheme,
  DifferentialNormalizationMethod,
} from "../../types/gazeHeatmapTypes";
import { useSceneGazeHeatmapData } from "../../hooks/useGazeHeatmapData";
import {
  useHeatmapGeneration,
  useHeatmapColorScheme,
  useHeatmapOpacity,
} from "../../hooks/useHeatmapGeneration";
import { calculateVideoSegments, filterPointsByTimeRange } from "../../utils/coordinateConversion";
import EquirectangularImage from "./EquirectangularImage";
import GazeHeatmapCanvas from "./GazeHeatmapCanvas";
import HeatmapLegend from "./HeatmapLegend";
import DifferentialHeatmapView from "./DifferentialHeatmapView";
import { COMPARISON_COLORS } from "../../utils/comparisonColors";

interface GroupHeatmapProps {
  contentId: number;
  groupName: string;
  groupColor: string;
  memberIds: (number | string)[];
  colorScheme: HeatmapColorScheme;
  opacity: number;
  currentSegment: number;
  segments: VideoSegment[];
  onImageLoad: () => void;
}

/**
 * Individual group heatmap panel for comparison view
 */
const GroupHeatmap: React.FC<GroupHeatmapProps> = ({
  contentId,
  groupName,
  groupColor,
  memberIds,
  colorScheme,
  opacity,
  currentSegment,
  segments,
  onImageLoad,
}) => {
  const { t } = useTranslation();
  const [imageLoaded, setImageLoaded] = useState(false);

  const { data, isLoading, error } = useSceneGazeHeatmapData({
    contentId,
    groupMemberIds: memberIds,
    enabled: true,
  });

  // Filter points by segment
  const filteredPoints = useMemo(() => {
    if (!data?.points) return [];
    if (segments.length === 0) return data.points;

    const segment = segments[currentSegment];
    if (!segment) return data.points;

    return filterPointsByTimeRange(data.points, segment.startTime, segment.endTime);
  }, [data?.points, segments, currentSegment]);

  // Memoize config to prevent infinite re-renders
  const heatmapConfig = useMemo(() => ({ colorScheme }), [colorScheme]);

  // Generate heatmap (with scene rotation offset for coordinate conversion)
  const { imageData: heatmapImageData, isGenerating } = useHeatmapGeneration({
    points: filteredPoints,
    config: heatmapConfig,
    enabled: imageLoaded && filteredPoints.length > 0,
    sceneRotationY: data?.sceneMedia?.rotationY || 0,
  });

  const handleImageLoad = () => {
    setImageLoaded(true);
    onImageLoad();
  };

  if (isLoading) {
    return (
      <div className="flex flex-col">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: groupColor }} />
          <span className="font-medium text-gray-700">{groupName}</span>
        </div>
        <div className="aspect-[2/1] bg-gray-100 rounded-lg flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600" />
        </div>
      </div>
    );
  }

  if (error || !data?.sceneMedia) {
    return (
      <div className="flex flex-col">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: groupColor }} />
          <span className="font-medium text-gray-700">{groupName}</span>
        </div>
        <div className="aspect-[2/1] bg-gray-100 rounded-lg flex items-center justify-center">
          <p className="text-gray-500 text-sm">{t("statsNew.gazeHeatmap.noData")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      {/* Group header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: groupColor }} />
          <span className="font-medium text-gray-700">{groupName}</span>
          <span className="text-sm text-gray-500">
            ({data.sessionCount} {t("statsNew.gazeHeatmap.sessions")})
          </span>
        </div>
        <span className="text-sm text-gray-500">
          {filteredPoints.length} {t("statsNew.gazeHeatmap.points")}
        </span>
      </div>

      {/* Heatmap viewer */}
      <div className="relative aspect-[2/1] bg-gray-100 rounded-lg overflow-hidden">
        <EquirectangularImage
          src={data.sceneMedia.url}
          alt={data.sceneName}
          onLoad={handleImageLoad}
        />
        <GazeHeatmapCanvas imageData={heatmapImageData} opacity={opacity} />

        {isGenerating && (
          <div className="absolute top-2 right-2 bg-white/80 rounded px-2 py-1">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600" />
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Comparison view showing heatmaps from multiple groups side by side
 */
const HeatmapComparisonView: React.FC<HeatmapComparisonViewProps> = ({
  contentId,
  groups,
  segmentDuration = 10,
  syncSegments: initialSyncSegments = true,
  colorScheme: initialColorScheme,
  onClose,
}) => {
  const { t } = useTranslation();

  // State
  const [currentSegment, setCurrentSegment] = useState(0);
  const [syncSegments, setSyncSegments] = useState(initialSyncSegments);
  const [viewMode, setViewMode] = useState<ComparisonViewMode>("sideBySide");
  const [differentialColorScheme, setDifferentialColorScheme] =
    useState<DifferentialColorScheme>("redBlue");
  const [differentialNormalization, setDifferentialNormalization] =
    useState<DifferentialNormalizationMethod>("perSample");

  // Persisted preferences
  const [colorScheme, setColorScheme] = useHeatmapColorScheme(initialColorScheme || "thermal");
  const [opacity, setOpacity] = useHeatmapOpacity(1.0);

  // Check if differential mode is available (exactly 2 groups)
  const canShowDifferential = groups.length === 2;

  // Fetch first group to get media info for segments
  const firstGroupQuery = useSceneGazeHeatmapData({
    contentId,
    groupMemberIds: groups[0]?.memberIds,
    enabled: groups.length > 0,
  });

  // Fetch second group for differential view
  const secondGroupQuery = useSceneGazeHeatmapData({
    contentId,
    groupMemberIds: groups[1]?.memberIds,
    enabled: groups.length > 1 && viewMode === "differential",
  });

  // Calculate video segments
  const isVideo =
    firstGroupQuery.data?.sceneMedia?.type === "video" &&
    (firstGroupQuery.data?.sceneMedia?.duration || 0) > 0;
  const videoDuration = firstGroupQuery.data?.sceneMedia?.duration || 0;

  const segments: VideoSegment[] = useMemo(() => {
    if (!isVideo || videoDuration <= 0) return [];
    return calculateVideoSegments(videoDuration, segmentDuration).map((s) => ({
      ...s,
      pointCount: 0, // Will be filled per group
    }));
  }, [isVideo, videoDuration, segmentDuration]);

  // Handle segment change
  const handleSegmentChange = useCallback(
    (segment: number) => {
      if (syncSegments) {
        setCurrentSegment(segment);
      }
    },
    [syncSegments],
  );

  // Handle group image load (could be used for loading state tracking)
  const handleGroupImageLoad = useCallback(() => {
    // Image loaded - could track loading state here if needed
  }, []);

  // Assign colors to groups
  const groupsWithColors = useMemo(() => {
    return groups.map((group, index) => ({
      ...group,
      color: group.color || COMPARISON_COLORS[index % COMPARISON_COLORS.length].primary,
    }));
  }, [groups]);

  // Grid columns based on number of groups
  const gridCols = groups.length === 2 ? "grid-cols-2" : "grid-cols-3";

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4">
          <h3 className="text-lg font-semibold text-gray-900">
            {t("statsNew.gazeHeatmap.comparisonTitle")}
          </h3>

          {/* View mode toggle - only show if 2 groups */}
          {canShowDifferential && (
            <div className="flex items-center bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode("sideBySide")}
                className={`px-3 py-1 text-sm rounded-md transition-colors ${
                  viewMode === "sideBySide"
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                {t("statsNew.differential.sideBySide")}
              </button>
              <button
                onClick={() => setViewMode("differential")}
                className={`px-3 py-1 text-sm rounded-md transition-colors ${
                  viewMode === "differential"
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                {t("statsNew.differential.differential")}
              </button>
            </div>
          )}
        </div>

        <div className="flex items-center gap-4">
          {/* Sync toggle - only in side-by-side mode */}
          {viewMode === "sideBySide" && isVideo && segments.length > 1 && (
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={syncSegments}
                onChange={(e) => setSyncSegments(e.target.checked)}
                className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
              />
              <span className="text-sm text-gray-600">
                {t("statsNew.gazeHeatmap.syncSegments")}
              </span>
            </label>
          )}

          {/* Color scheme selector - different options for each mode */}
          {viewMode === "sideBySide" ? (
            <select
              value={colorScheme}
              onChange={(e) => setColorScheme(e.target.value as HeatmapColorScheme)}
              className="border border-gray-300 rounded-lg px-2 py-1 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
            >
              <option value="green">{t("statsNew.gazeHeatmap.schemeGreen")}</option>
              <option value="thermal">{t("statsNew.gazeHeatmap.schemeThermal")}</option>
              <option value="viridis">{t("statsNew.gazeHeatmap.schemeViridis")}</option>
            </select>
          ) : (
            <select
              value={differentialColorScheme}
              onChange={(e) =>
                setDifferentialColorScheme(e.target.value as DifferentialColorScheme)
              }
              className="border border-gray-300 rounded-lg px-2 py-1 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
            >
              <option value="redBlue">{t("statsNew.differential.redBlue")}</option>
              <option value="redGray">{t("statsNew.differential.redGray")}</option>
              <option value="purpleGreen">{t("statsNew.differential.purpleGreen")}</option>
            </select>
          )}

          {/* Normalization selector - only in differential mode */}
          {viewMode === "differential" && (
            <select
              value={differentialNormalization}
              onChange={(e) =>
                setDifferentialNormalization(e.target.value as DifferentialNormalizationMethod)
              }
              className="border border-gray-300 rounded-lg px-2 py-1 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
            >
              <option value="perSample">{t("statsNew.differential.normPerSample")}</option>
              <option value="maxDensity">{t("statsNew.differential.normMaxDensity")}</option>
              <option value="zscore">{t("statsNew.differential.normZScore")}</option>
            </select>
          )}

          {/* Opacity slider */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">{t("statsNew.gazeHeatmap.opacity")}:</span>
            <input
              type="range"
              min="0"
              max="100"
              value={Math.round(opacity * 100)}
              onChange={(e) => setOpacity(Number(e.target.value) / 100)}
              className="w-20 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-green-600"
            />
            <span className="text-sm text-gray-600 w-8">{Math.round(opacity * 100)}%</span>
          </div>

          {/* Close button */}
          {onClose && (
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              aria-label={t("general.close")}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Video segment controls (shared when synced) */}
      {isVideo && segments.length > 1 && syncSegments && (
        <div className="mb-4">
          <div className="flex items-center justify-center gap-4">
            <button
              onClick={() => handleSegmentChange(Math.max(0, currentSegment - 1))}
              disabled={currentSegment === 0}
              className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 text-gray-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>

            <span className="text-sm font-medium text-gray-700">
              Segment {currentSegment + 1}/{segments.length}
            </span>

            <button
              onClick={() => handleSegmentChange(Math.min(segments.length - 1, currentSegment + 1))}
              disabled={currentSegment === segments.length - 1}
              className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 text-gray-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Main content - conditional rendering based on view mode */}
      {viewMode === "sideBySide" ? (
        <>
          {/* Groups grid */}
          <div className={`grid ${gridCols} gap-4 flex-1`}>
            {groupsWithColors.map((group) => (
              <GroupHeatmap
                key={group.id}
                contentId={contentId}
                groupName={group.name}
                groupColor={group.color}
                memberIds={group.memberIds}
                colorScheme={colorScheme}
                opacity={opacity}
                currentSegment={currentSegment}
                segments={segments}
                onImageLoad={handleGroupImageLoad}
              />
            ))}
          </div>

          {/* Legend */}
          <div className="mt-4">
            <HeatmapLegend colorScheme={colorScheme} />
          </div>
        </>
      ) : (
        /* Differential view */
        <div className="flex-1">
          {firstGroupQuery.isLoading || secondGroupQuery.isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="flex items-center gap-3">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600" />
                <span className="text-gray-500">{t("statsNew.differential.loading")}</span>
              </div>
            </div>
          ) : firstGroupQuery.data?.sceneMedia ? (
            <DifferentialHeatmapView
              mediaUrl={firstGroupQuery.data.sceneMedia.url}
              sceneName={firstGroupQuery.data.sceneName}
              sceneRotationY={firstGroupQuery.data.sceneMedia.rotationY}
              groupA={{
                name: groupsWithColors[0]?.name || "Group A",
                color: groupsWithColors[0]?.color,
                points: firstGroupQuery.data.points || [],
              }}
              groupB={{
                name: groupsWithColors[1]?.name || "Group B",
                color: groupsWithColors[1]?.color,
                points: secondGroupQuery.data?.points || [],
              }}
              opacity={opacity}
              colorScheme={differentialColorScheme}
              normalization={differentialNormalization}
            />
          ) : (
            <div className="flex items-center justify-center h-64">
              <span className="text-gray-500">{t("statsNew.gazeHeatmap.noMedia")}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default HeatmapComparisonView;
